import { yupResolver } from "@hookform/resolvers/yup";
import Button from "@src/components/Button/Button";
import FormField from "@src/components/FormField/FormField";
import Input from "@src/components/Input/Input";
import useRedirectIfAuthenticated from "@src/hooks/useRedirectIfAuthenticated";
import Page from "@src/layout/Page";
import { requestPasswordReset, resetPassword } from "@src/services/requests/user";
import { useUserStore } from "@src/stores/userStore";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import * as yup from "yup";

const ResetPassword = () => {
    const navigate = useNavigate();
    const token = useUserStore((state) => state.token);
    useEffect(() => {
        useRedirectIfAuthenticated(navigate);
    }, [token]);
    const { resetToken } = useParams();

    // Query setup
    const requestMutation = useMutation({
        mutationFn: requestPasswordReset,
        onSuccess: (data) => {
            toast.success(data);
        },
    });
    const resetMutation = useMutation({
        mutationFn: resetPassword,
        onSuccess: (data) => {
            toast.success(data);
        },
    });

    // Form setup
    const requestSchema = yup.object().shape({
        email: yup
            .string()
            .email("Please provide a valid e-mail")
            .required()
            .transform((value: string) => value.toLowerCase()),
    });

    const resetSchema = yup.object().shape({
        password: yup
            .string()
            .required("Password required")
            .min(12, "Password must be at least 12 characters")
            .matches(
                /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{12,}$/,
                {
                    message:
                        "Password must be 12 characters long, at least one upper case letter, one lower case letter, one number and one special character",
                },
            ),
        confirm_password: yup
            .string()
            .required("Please confirm your password")
            .oneOf([yup.ref("password")], "Passwords must match"),
    });
    const requestMethods = useForm({ resolver: yupResolver(requestSchema) });
    const resetMethods = useForm({ resolver: yupResolver(resetSchema) });
    interface requestFormType extends yup.InferType<typeof requestSchema> { }
    interface resetFormType extends yup.InferType<typeof resetSchema> { }
    function requestSubmit(data: requestFormType) {
        requestMutation.mutate(data);
    }
    function resetSubmit(data: resetFormType) {
        resetMutation.mutate({ token: resetToken!, ...data });
    }
    // UI
    return (
        <Page>
            <header>
                <h2>Forgot password ?</h2>
            </header>
            <section>
                {resetToken == null ? (
                    <>
                        <p className="info">
                            Enter your e-mail address to get a reset link
                        </p>

                        <form
                            onSubmit={requestMethods.handleSubmit(
                                requestSubmit,
                            )}
                        >
                            <FormField
                                name="email"
                                errors={requestMethods.formState.errors}
                            >
                                <Input
                                    register={requestMethods.register}
                                    name="email"
                                />
                            </FormField>
                            <Button
                                isLoading={requestMutation.isPending}
                                preset="secondary"
                                type="submit"
                            >
                                Request reset link
                            </Button>
                        </form>
                    </>
                ) : (
                    <>
                        {resetMutation.isSuccess ? (
                            <div>
                                <p>
                                    Your password has been reset! Try to log
                                    back in.
                                </p>
                                <br />
                                <p>
                                    If you still have issues logging in, please{" "}
                                    <Link to="/contact">contact support</Link>{" "}
                                    and I'll do what I can.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={resetMethods.handleSubmit(
                                    resetSubmit,
                                )}
                            >
                                Create new password
                                <FormField
                                    name="password"
                                    errors={resetMethods.formState.errors}
                                >
                                    <Input
                                        type="password"
                                        name="password"
                                        placeholder="Your password"
                                        register={resetMethods.register}
                                        required
                                    />
                                </FormField>
                                <FormField
                                    name="confirm_password"
                                    errors={resetMethods.formState.errors}
                                    hideLabel={true}
                                >
                                    <label
                                        htmlFor="confirm_password"
                                        className="input__label"
                                    >
                                        Confirm your password
                                    </label>
                                    <Input
                                        type="password"
                                        name="confirm_password"
                                        placeholder="Confirm password"
                                        register={resetMethods.register}
                                        required
                                    />
                                </FormField>
                                <Button
                                    isLoading={resetMutation.isPending}
                                    preset="secondary"
                                    type="submit"
                                >
                                    Reset password
                                </Button>
                            </form>
                        )}
                    </>
                )}
            </section>
        </Page>
    );
};
export default ResetPassword;
